import { useSelector } from "react-redux";
import { useState, useCallback, useRef, useEffect } from "react";
import { selector as singlewireTokenApi } from "../addSinglewireTokenModal/addSinglewireTokenSlice";
import { selector as groupApi } from "../../pages/deviceManagement/groupSlice";
import {
  fetchSinglewireSiteData,
  selector as singlewireSiteApi,
} from "../../pages/integrations/singlewire/listSinglewireSiteSlice";
import { IGroup } from "../../common/types/groupTypes";
import { useDispatch } from "react-redux";
import { mapSinglewireSiteData } from "./mapSinglewireSitesSlice";
import { isSuccessResponse } from "../../common/utils";
import useNotification from "../notification/useNotification";

interface ISiteData {
  siteId: string;
  siteName: string;
  groupId: string;
  groupName: string;
  _id: string;
}

export const useMapSinglewireSiteModal = () => {
  const dispatch: any = useDispatch();
  const [showModel, setShowModel] = useState<boolean>(false);
  const [showTooltip, setShowTooltip] = useState<boolean>(false);
  const isTokenAdded: boolean = Boolean(
    useSelector(singlewireTokenApi)?.userId
  );
  const tooltipRef = useRef<HTMLDivElement | null>(null);
  const groupsDetails: IGroup[] = useSelector(groupApi).groups;
  const sitesDetails = useSelector(singlewireSiteApi).singlewireSite;
  const userId = sitesDetails?.userId;
  let groups: string[];
  let sitesData: ISiteData[] = [];

  if (sitesDetails && sitesDetails.sites) {
    sitesData = sitesDetails.sites;
  }

  useEffect(() => {
    if (sitesDetails && groupsDetails) {
      const initialAssignments = sitesDetails.sites.reduce((acc, site) => {
        acc[site.siteId] = site.groupName || null;
        return acc;
      }, {});
      setAssignments(initialAssignments);
    }
  }, [sitesDetails, groupsDetails]);
  
  useEffect(() => {
    if (groupsDetails) {
      groups = groupsDetails.map((item) => item.displayName);
      setAvailableGroups(groups);
    }
  }, [groupsDetails]);

  const key: Record<string, string | null> = sitesData.reduce(
    (siteAccumulator, currentSite) => {
      if (currentSite.groupId !== "" && currentSite.groupName !== "") {
        siteAccumulator[currentSite.siteId] = currentSite.groupName;
      } else {
        siteAccumulator[currentSite.siteId] = null;
      }
      return siteAccumulator;
    },
    {}
  );

  const [assignments, setAssignments] =
    useState<Record<string, string | null>>(key);
  const [availableGroups, setAvailableGroups] = useState<string[] | null>(
    groups
  );

  const handleClose = useCallback(() => {
    setShowModel(false);
  }, []);

  const handleShow = useCallback(() => {
    const initialAssignments = sitesData.reduce((acc, site) => {
      acc[site.siteId] = site.groupName || null;
      return acc;
    }, {});
    setAssignments(initialAssignments);
    setShowModel(true);
  }, [sitesData]);

  const { showSuccessNotification, showWarningNotification } =
    useNotification();

  const handleMapSinglewireSites = useCallback(() => {
    const mappedSitesDetails = sitesData.map((site) => {
      const assignedGroupName = assignments[site.siteId] || "";
      const assignedGroup = groupsDetails.find(
        (group) => group.displayName === assignedGroupName
      );
      return {
        siteId: site.siteId,
        siteName: site.siteName,
        groupId: assignedGroup ? assignedGroup.orgId : "",
        groupName: assignedGroupName,
      };
    });

    const payload = {
      userId: userId,
      sites: { sites: mappedSitesDetails },
    };

    dispatch(mapSinglewireSiteData(payload)).then((response) => {
      if (isSuccessResponse(response.payload)) {
        dispatch(fetchSinglewireSiteData());
        showSuccessNotification(response.payload.message);
        setShowModel(false);
      } else {
        showWarningNotification(response.payload.response.data.message);
      }
    });
  }, [
    sitesData,
    assignments,
    groupsDetails,
    userId,
    dispatch,
    showSuccessNotification,
    showWarningNotification,
  ]);

  const handleSelectGroup = (siteId: string, groupName: string): void => {
    const updatedAssignments: Record<string, string | null> = Object.keys(
      assignments
    ).reduce((assignmentAccumulator, currentAssignmentKey) => {
      if (assignments[currentAssignmentKey] === groupName) {
        assignmentAccumulator[currentAssignmentKey] = null;
      } else {
        assignmentAccumulator[currentAssignmentKey] =
          assignments[currentAssignmentKey];
      }
      return assignmentAccumulator;
    }, {});

    setAssignments({
      ...updatedAssignments,
      [siteId]: groupName,
    });
  };

  return {
    showModel,
    handleShow,
    handleClose,
    showTooltip,
    setShowTooltip,
    tooltipRef,
    isTokenAdded,
    handleSelectGroup,
    assignments,
    setAssignments,
    availableGroups,
    sitesData,
    handleMapSinglewireSites,
  };
};
